import {
  Box,
  Stack,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { observer } from 'mobx-react'
import React, { FC } from 'react'
import NewTableRow from '../../../../components/NewCustomTableElements/NewTableRow'
import NotFoundTableRow from '../../../../components/NewCustomTableElements/NotFoundTableRow'
import TableLoaderElement from '../../../../components/NewCustomTableElements/TableLoaderElement'
import HeadPagination from '../../../../components/TableElements/Pagination/HeadPagination'
import NewPagination from '../../../../components/TableElements/Pagination/NewPagination'
import TableHeaderCell from '../../../../components/TableElements/TableHeaderCell'
import { isAdmin } from '../../../../services/AccountService'
import { PaginationStoreType } from '../../../../stores/NewPaginationStore'
import TableScrollButtons from '../../../../utils/components/CustomTable/components/TableScrollButtons/TableScrollButtons'
import { tableSx } from '../../../Customers/consts/table.styles'
import { tableWrapperSx } from '../../../SingleCustomer/consts/table.styles'
import { getHeadTypographySx } from '../../../SingleServiceReport/styles/table.styles'
import GroupOperations from './components/GroupOperations'
import NewHeadCheckbox from './components/NewHeadCheckbox'
import {
  emptyTableHeadKeys,
  getMapHeadTitles,
  widthMap,
} from './consts/table.consts'
import { getCellContent } from './helpers/getCellContent'
import { useNewRequestsTable } from './hooks/useNewRequestsTable'
import { RequestTableParamsType } from './types/new-requests-table.types'

interface RequestsNewTableProps {
  paginationStore: PaginationStoreType<RequestTableParamsType>
  checkboxStore: any
}

const RequestsNewTable: FC<RequestsNewTableProps> = ({
  paginationStore,
  checkboxStore,
}) => {
  const {
    cellClasses,
    columns,
    data,
    isLoading,
    isSmallLength,
    onRowClick,
    getOnSort,
    selectedRow,
    setRenderTable,
    sort,
  } = useNewRequestsTable(paginationStore, checkboxStore)
  const mapHeadTitles = getMapHeadTitles()
  const firstStatus = isAdmin()
    ? data?.content.find((el) => [1, 2, 4].includes(el.status))?.status
    : data?.content[0]?.status

  const GroupOperationFeature = (
    <GroupOperations
      checkboxStore={checkboxStore}
      paginationStore={paginationStore}
    />
  )

  return (
    <Box sx={tableWrapperSx}>
      <HeadPagination
        contentLength={data?.content?.length ?? 0}
        totalSize={data?.totalSize ?? 0}
        rowsPerPageOptions={[50, 100, 200, 300, 400, 500]}
        store={paginationStore}
        setRenderTable={setRenderTable}
        GroupOperationFeature={GroupOperationFeature}
      />
      <Box
        sx={{ overflow: isLoading ? 'hidden' : 'auto' }}
        id={'custom-table-container'}
      >
        <Table sx={tableSx} aria-label='simple table'>
          <TableHead>
            <TableRow>
              {columns.map((key: string) => {
                if (emptyTableHeadKeys.includes(key))
                  return (
                    <TableHeaderCell
                      paddingLeftKeys={['isExpiredAssign']}
                      width={key in widthMap ? widthMap[key] : undefined}
                    />
                  )

                if (key === 'checkbox') {
                  return (
                    <NewHeadCheckbox
                      firstStatus={firstStatus ?? 0}
                      data={data}
                      checkboxStore={checkboxStore}
                    />
                  )
                }

                return (
                  <TableHeaderCell
                    align={'left'}
                    key={key}
                    sortKey={getSortKey(key)}
                    onSort={getOnSort(key)}
                    sort={sort}
                    customPadding='8px'
                    width={key in widthMap ? widthMap[key] : undefined}
                  >
                    <Typography
                      variant={'body1'}
                      sx={getHeadTypographySx(cellClasses)}
                    >
                      {mapHeadTitles[key as unknown as 'status']}
                    </Typography>
                  </TableHeaderCell>
                )
              })}
            </TableRow>
          </TableHead>
          {isLoading && <TableLoaderElement colSpan={columns.length} />}
          {!isLoading && (
            <TableBody>
              {data?.content?.map((el: any, idx: number) => (
                <NewTableRow
                  onClick={() => onRowClick(el)}
                  outline={selectedRow?.id === el.id}
                  key={el.id}
                >
                  {columns.map((key) =>
                    getCellContent(key, el, idx, data, checkboxStore),
                  )}
                </NewTableRow>
              ))}
              {!data?.content.length && (
                <NotFoundTableRow columnsLength={columns.length} />
              )}
            </TableBody>
          )}
        </Table>
      </Box>
      <Stack
        direction={'row'}
        justifyContent={'space-between'}
        padding={'36px 16px 16px 16px'}
      >
        {GroupOperationFeature}
        <NewPagination
          hidePageSign={isSmallLength}
          totalSize={data?.totalSize ?? 0}
          rowsPerPageOptions={[50, 100, 200, 300, 400, 500]}
          store={paginationStore}
          setRenderTable={setRenderTable}
        />
      </Stack>
      {!isLoading && !!data?.content?.length && (
        <TableScrollButtons rowsLength={data?.content.length} />
      )}
    </Box>
  )
}

export const getSortKey = (key: string) => {
  if (key === 'name') return 'fullName'
  if (key === 'installationDate') return 'installed'
  if (key === 'financialSourceId') return 'financial-source'
  return key
}

export default observer(RequestsNewTable)
